<template>
  <div>
    <div class='flex w-full overflow-x-auto'>
      <nav
        class='relative z-0 flex w-full items-center'
        aria-label='Tabs'
      >
        <button
          v-for='tab in getViewTabs()'
          :key='tab.id'
          :class='[tab.active ? "text-gray-900 border-b-2 border-gray-500" : "text-gray-500 hover:text-gray-700 border-gray-100 focus-visible:border-b-2 focus-visible:border-gray-300", "shrink-0 border-b-2 group relative w-full flex-1 flex flex-row items-center max-w-fit lg:max-w-auto justify-center gap-x-2 bg-transparent py-3 px-5 text-sm font-medium text-center hover:bg-gray-100 focus:z-10 focus-visible:outline-none"]'
          :aria-current='tab.active ? "page" : undefined'
          @click.exact='setActiveTab(tab.name || tab.value)'
          @click.meta.exact='openTabInNewBrowserTab(tab.name || tab.value)'
          @click.ctrl.exact='openTabInNewBrowserTab(tab.name || tab.value)'
        >
          <slot
            :icon='tab.icon'
            name='icon'
          />
          <span class='whitespace-nowrap'>{{ tab.label }}</span>

        </button>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
  import { Tab } from '@/types/general/TabType'
  import { defineComponent, onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useModuleStore } from '@/store/module'
  import { passesDisplayConditions } from '@/utils/displayConditions'
  import { useUserStore } from '@/store/user'
  import { useQueryState } from '@/compositions/useQueryState'

  export default defineComponent({
    // eslint-disable-next-line vue/multi-word-component-names
    name: 'Tabs',
    props: {
      tabs: {
        type: Object,
        required: true,
      },
      defaultTab: {
        type: String,
        required: true,
      },
      withRouter: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['setActiveTab'],
    setup(props, { emit }) {
      const tabsRef = ref(props.tabs)
      const route = useRoute()
      const router = useRouter()
      const activeTab = ref('')
      const moduleStore = useModuleStore()
      const userStore = useUserStore()

      const { resetQuery } = useQueryState()

      onMounted(() => {
        if (props.withRouter) activeTab.value = route.params.tab?.toString() || props.defaultTab
        else activeTab.value = props.defaultTab
        setActiveTab(activeTab.value)
      })

      const getViewTabs = () => {
        return tabsRef.value.filter((tab: Tab) => {
          if (!passesDisplayConditions(useModuleStore().detailData, tab.display_conditions)) {
            return false
          }

          if (userStore.user?.client_categories_hidden && tab.name === 'clients-categories-relation') {
            return false
          }

          return route.meta.view === 'index' ? (tab.view === 'index') : (tab.view !== 'index')
        })
      }

      const setActiveTab = async (tabName: string) => {
        const shouldChangeQuery = moduleStore.activeTab ? tabName !== moduleStore.activeTab.name : false

        props.tabs.forEach((propTab: Tab) => {
          propTab.active = false

          if (tabName && (propTab.name || propTab.key) === tabName) {
            propTab.active = true
            moduleStore.activeTab = propTab
          }
        })
        if (tabName) activeTab.value = tabName

        if (props.withRouter && (route.params.tab?.toString() || tabName !== props.defaultTab)) {
          if (shouldChangeQuery) {
            await resetQuery()
          }

          router.push(
            {
              params: {
                id: route.params.id,
                tab: tabName,
              },
              query: shouldChangeQuery ? {
              } : route.query,
            }
          )
        }

        emit('setActiveTab', tabName)
      }

      const openTabInNewBrowserTab = (newTab: string) => {
        const routeLink = router.resolve('/' + router.currentRoute.value.meta.module + '/' + route.params.id + '/' + newTab).href

        window.open(routeLink, '_blank')
      }

      watch(() => props.tabs, (newTab) => {
        setActiveTab(activeTab.value)

        tabsRef.value = newTab
      })

      watch(() => route.params.tab, (newTab, oldTab) => {
        if (activeTab.value === newTab) return

        // Handle history.back() when route changed from empty tab to detail
        if (oldTab === props.defaultTab && newTab === '') return router.go(-1)

        if (newTab) {
          if (props.withRouter) setActiveTab(newTab?.toString())
        } else setActiveTab(props.defaultTab)
      })

      return {
        tabsRef,
        setActiveTab,
        getViewTabs,
        openTabInNewBrowserTab,
        activeTab,
      }
    },
  })
</script>
